import { Globe2, Headphones, Languages, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import HeroBanner from "../components/HeroBanner";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import { api } from "../lib/api";
import { Currency } from "../lib/currency";
import { t } from "../lib/i18n";
import { sampleProducts } from "./_shared";

type HomeSection = {
  id: number;
  section_type: string;
  title?: string;
  subtitle?: string;
  image_url?: string;
  button_text?: string;
  button_link?: string;
  background_color?: string;
  config_json?: string;
};

const categoryCards = [
  { key: "electronics", count: "38", image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80", slug: "electronics" },
  { key: "lifestyle", count: "26", image: "https://images.unsplash.com/photo-1602143407151-7111542de6e8?auto=format&fit=crop&w=900&q=80", slug: "lifestyle" },
  { key: "workspace", count: "31", image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=80", slug: "workspace" },
  { key: "travel", count: "22", image: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=900&q=80", slug: "travel" }
];

function CategoryGrid({ lang, title, subtitle }: { lang?: string; title?: string; subtitle?: string }) {
  return (
    <section className="section-space">
      <div className="container-page">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div><p className="text-sm font-bold uppercase tracking-[0.18em] text-[#C89B5C]">{title || t(lang, "shopByCategory")}</p><h2 className="mt-2 text-3xl font-extrabold text-[#0F172A] md:text-5xl md:font-black">{subtitle || t(lang, "dailyEssentials")}</h2></div>
          <Link className="btn-secondary hidden md:inline-flex" to="products">{t(lang, "viewAll")}</Link>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {categoryCards.map((category) => (
            <Link key={category.slug} to={`category/${category.slug}`} className="group relative min-h-56 overflow-hidden rounded-[24px] shadow-[0_18px_45px_rgba(15,23,42,0.08)] md:min-h-80">
              <img src={category.image} alt={t(lang, category.key)} className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/82 via-[#0F172A]/20 to-transparent" />
              <div className="absolute bottom-0 p-5 text-white"><h3 className="text-xl font-black md:text-2xl">{t(lang, category.key)}</h3><p className="mt-1 text-sm text-slate-100">{category.count} {t(lang, "productsCount")}</p></div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductGrid({ currency, lang, title, subtitle }: { currency: Currency; lang?: string; title?: string; subtitle?: string }) {
  return (
    <section className="container-page section-space pt-0">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div><p className="text-sm font-bold uppercase tracking-[0.18em] text-[#C89B5C]">{title || t(lang, "featuredProducts")}</p><h2 className="mt-2 text-3xl font-extrabold text-[#0F172A] md:text-5xl md:font-black">{subtitle || t(lang, "newArrivals")}</h2></div>
        <Link className="btn-secondary hidden md:inline-flex" to="products">{t(lang, "shopNow")}</Link>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{sampleProducts.map((p) => <ProductCard key={p.id} product={p} currency={currency} />)}</div>
    </section>
  );
}

function RenderSection({ section, currency, lang }: { section: HomeSection; currency: Currency; lang?: string }) {
  if (section.section_type === "hero") return <HeroBanner custom={section} />;
  if (section.section_type === "category_grid") return <CategoryGrid lang={lang} title={section.title} subtitle={section.subtitle} />;
  if (["featured_products", "product_collection"].includes(section.section_type)) return <ProductGrid currency={currency} lang={lang} title={section.title} subtitle={section.subtitle} />;
  if (section.section_type === "benefits") return <Benefits lang={lang} title={section.title} subtitle={section.subtitle} />;
  if (section.section_type === "newsletter") return <Newsletter lang={lang} title={section.title} subtitle={section.subtitle} />;
  if (section.section_type === "custom_html") return <section className="container-page pb-12"><div className="soft-card p-6" dangerouslySetInnerHTML={{ __html: section.subtitle || "" }} /></section>;
  return (
    <section className="container-page pb-12 md:pb-20">
      <div className="grid gap-6 rounded-[32px] border border-[#0F172A]/10 p-6 shadow-[0_24px_70px_rgba(15,23,42,0.08)] md:grid-cols-2 md:p-10" style={{ background: section.background_color || "#FFFFFF" }}>
        <img src={section.image_url || "/placeholder.webp"} className="aspect-[16/10] w-full rounded-[26px] object-cover" />
        <div className="self-center"><h2 className="text-3xl font-black md:text-5xl">{section.title || t(lang, "limitedDeals")}</h2><p className="mt-4 text-slate-600">{section.subtitle || t(lang, "limitedDealsText")}</p>{section.button_text ? <Link className="btn-primary mt-7" to={section.button_link || "products"}>{section.button_text}</Link> : null}</div>
      </div>
    </section>
  );
}

function Benefits({ lang, title, subtitle }: { lang?: string; title?: string; subtitle?: string }) {
  return (
    <section className="container-page pb-12 md:pb-20">
      <div className="mb-8"><p className="text-sm font-bold uppercase tracking-[0.18em] text-[#C89B5C]">{title || t(lang, "whyChooseUs")}</p><h2 className="mt-2 text-3xl font-extrabold text-[#0F172A] md:text-5xl md:font-black">{subtitle || t(lang, "confidentBuying")}</h2></div>
      <div className="grid gap-5 md:grid-cols-4">{[[ShieldCheck, "secureManualPayment", "confidentBuyingText1"], [Languages, "multilingualShopping", "confidentBuyingText2"], [Globe2, "globalProductCatalog", "confidentBuyingText3"], [Headphones, "buyerSupport", "confidentBuyingText4"]].map(([Icon, heading, body]) => <div className="soft-card p-6" key={String(heading)}>{typeof Icon !== "string" && <Icon className="mb-5 text-[#C89B5C]" size={28} />}<h3 className="font-black text-[#0F172A]">{t(lang, heading as string)}</h3><p className="mt-3 text-sm leading-6 text-slate-500">{t(lang, body as string)}</p></div>)}</div>
    </section>
  );
}

function Newsletter({ lang, title, subtitle }: { lang?: string; title?: string; subtitle?: string }) {
  return (
    <section className="container-page pb-12 md:pb-20">
      <div className="grid gap-6 rounded-[32px] bg-white p-7 shadow-[0_24px_70px_rgba(15,23,42,0.10)] md:grid-cols-[1fr_460px] md:p-10">
        <div><p className="text-sm font-bold uppercase tracking-[0.18em] text-[#C89B5C]">Newsletter</p><h2 className="mt-2 text-3xl font-extrabold text-[#0F172A] md:text-5xl md:font-black">{title || t(lang, "newsletterTitle")}</h2><p className="mt-4 text-slate-600">{subtitle || t(lang, "newsletterText")}</p></div>
        <form className="flex flex-col gap-3 self-center sm:flex-row"><input className="input" placeholder={t(lang, "emailAddress")} /><button className="btn-primary">{t(lang, "subscribe")}</button></form>
      </div>
    </section>
  );
}

function DefaultHome({ currency, lang }: { currency: Currency; lang?: string }) {
  return <><HeroBanner /><CategoryGrid lang={lang} /><ProductGrid currency={currency} lang={lang} /><RenderSection section={{ id: 0, section_type: "promo_image" }} currency={currency} lang={lang} /><ProductGrid currency={currency} lang={lang} title={t(lang, "bestSellers")} subtitle={t(lang, "customerFavorites")} /><Benefits lang={lang} /><Newsletter lang={lang} /></>;
}

export default function Home({ currency }: { currency: Currency }) {
  const { lang = "en" } = useParams();
  const [sections, setSections] = useState<HomeSection[] | null>(null);
  useEffect(() => { api<HomeSection[]>("/api/homepage").then(setSections).catch(() => setSections([])); }, []);
  return (
    <>
      <Seo title="SellerSupply Shop" description="Discover curated travel, workspace, and smart lifestyle products with simple checkout and multilingual shopping." image={sampleProducts[0].image} />
      {sections && sections.length ? sections.map((section) => <RenderSection key={section.id} section={section} currency={currency} lang={lang} />) : <DefaultHome currency={currency} lang={lang} />}
    </>
  );
}
